// VOY-39: Performance monitoring utilities for frontend
// Integrates with the browser Performance API

/**
 * Performance marker for tracking operations
 */
export class PerformanceTracker {
  private marks: Map<string, number> = new Map();
  private measurements: Array<{
    operation: string;
    duration: number;
    timestamp: string;
    success: boolean;
    error?: string;
  }> = [];

  /**
   * Start tracking an operation
   */
  start(operation: string): void {
    const markName = `${operation}-start`;
    performance.mark(markName);
    this.marks.set(operation, Date.now());
    console.log(`[Performance] 🏁 Started: ${operation}`);
  }

  /**
   * End tracking an operation and record duration
   */
  end(operation: string, success: boolean = true, error?: string): number {
    const endMarkName = `${operation}-end`;
    const startMarkName = `${operation}-start`;
    
    performance.mark(endMarkName);
    
    try {
      // Use performance.measure for precise timing
      performance.measure(operation, startMarkName, endMarkName);
      
      const measure = performance.getEntriesByName(operation, 'measure')[0];
      const duration = measure ? Math.round(measure.duration) : 0;
      
      // Record measurement
      this.measurements.push({
        operation,
        duration,
        timestamp: new Date().toISOString(),
        success,
        error
      });
      
      // Keep only last 100 measurements
      if (this.measurements.length > 100) {
        this.measurements.shift();
      }
      
      // Log result
      const statusEmoji = success ? '✅' : '❌';
      const durationColor = duration > 2000 ? '🔴' : duration > 1000 ? '🟡' : '🟢';
      console.log(`[Performance] ${statusEmoji} ${durationColor} ${operation} - ${duration}ms`);
      
      // Clean up marks
      performance.clearMarks(startMarkName);
      performance.clearMarks(endMarkName);
      performance.clearMeasures(operation);
      this.marks.delete(operation);
      
      return duration;
    } catch (error) {
      console.error(`[Performance] Failed to measure ${operation}:`, error);
      const fallbackDuration = Date.now() - (this.marks.get(operation) || Date.now());
      return fallbackDuration;
    }
  }

  /**
   * Get statistics for all measurements
   */
  getStats() {
    if (this.measurements.length === 0) {
      return {
        total_operations: 0,
        avg_duration_ms: 0,
        success_rate: 0,
        measurements: []
      };
    }

    const durations = this.measurements.map(m => m.duration);
    const successCount = this.measurements.filter(m => m.success).length;
    
    return {
      total_operations: this.measurements.length,
      avg_duration_ms: Math.round(durations.reduce((a, b) => a + b, 0) / durations.length),
      success_rate: Math.round((successCount / this.measurements.length) * 100),
      measurements: this.measurements.slice(-10) // Last 10
    };
  }

  /**
   * Get measurements for a specific operation
   */
  getOperationStats(operation: string) {
    const opMeasurements = this.measurements.filter(m => m.operation === operation);
    
    if (opMeasurements.length === 0) {
      return null;
    }

    const durations = opMeasurements.map(m => m.duration);
    const successCount = opMeasurements.filter(m => m.success).length;
    
    return {
      operation,
      count: opMeasurements.length,
      avg_duration_ms: Math.round(durations.reduce((a, b) => a + b, 0) / durations.length),
      min_duration_ms: Math.min(...durations),
      max_duration_ms: Math.max(...durations),
      success_rate: Math.round((successCount / opMeasurements.length) * 100),
      last_measurement: opMeasurements[opMeasurements.length - 1]
    };
  }

  /**
   * Clear all measurements
   */
  clear(): void {
    this.measurements = [];
    this.marks.clear();
    console.log('[Performance] 🧹 Cleared all measurements');
  }
}

// Global performance tracker instance
export const performanceTracker = new PerformanceTracker();

/**
 * Track a function execution with automatic timing
 */
export async function trackOperation<T>(
  operation: string,
  fn: () => Promise<T>
): Promise<T> {
  performanceTracker.start(operation);
  
  try {
    const result = await fn();
    performanceTracker.end(operation, true);
    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    performanceTracker.end(operation, false, errorMessage);
    throw error;
  }
}

/**
 * Fetch performance metrics from backend
 */
export async function fetchBackendMetrics(): Promise<unknown> {
  try {
    const response = await fetch('/api/metrics/performance');
    if (!response.ok) {
      throw new Error('Failed to fetch metrics');
    }
    return await response.json();
  } catch (error) {
    console.error('[Performance] Failed to fetch backend metrics:', error);
    return null;
  }
}

/**
 * Log performance summary to console
 */
export function logPerformanceSummary(): void {
  const stats = performanceTracker.getStats();
  
  console.log('\n' + '='.repeat(60));
  console.log('📊 PERFORMANCE SUMMARY');
  console.log('='.repeat(60));
  console.log(`Total Operations: ${stats.total_operations}`);
  console.log(`Avg Duration: ${stats.avg_duration_ms}ms`);
  console.log(`Success Rate: ${stats.success_rate}%`);
  console.log('='.repeat(60) + '\n');
  
  // Show last 5 measurements
  if (stats.measurements.length > 0) {
    console.log('Last 5 Operations:');
    stats.measurements.slice(-5).forEach(m => {
      const emoji = m.success ? '✅' : '❌';
      console.log(`  ${emoji} ${m.operation}: ${m.duration}ms ${m.error ? `(${m.error})` : ''}`);
    });
    console.log('');
  }
}

/**
 * Expose performance stats on window (for debugging in DevTools)
 */
if (typeof window !== 'undefined') {
  (window as Window & { __voyPerformance?: unknown }).__voyPerformance = {
    tracker: performanceTracker,
    getStats: () => performanceTracker.getStats(),
    logSummary: () => logPerformanceSummary(),
    fetchBackendMetrics
  };
  
  console.log('💡 Performance tools available: window.__voyPerformance');
}

